import { I18nProvider } from 'next-localization';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import { useEffect } from 'react';

import { usePdf } from 'src/hooks/usePdf';

import { waitUntilVisible } from 'src/utils/waitUntilVisible';

import { SitecorePageProps } from 'lib/page-props';

import ErrorBoundary from 'components/BaseHelpers/ErrorBoundary';

import '../styles/global/global.scss';

function App({ Component, pageProps }: AppProps<SitecorePageProps>): JSX.Element {
  useEffect(() => {
    waitUntilVisible();
  }, []);

  const { dictionary, ...rest } = pageProps;

  const isExperienceEditor = rest.layoutData?.sitecore?.context?.pageEditing;
  const isTcSite = rest.site?.name === 'tc';
  const googleTagManagerId = isTcSite ? 'GTM-5VSGDX8T' : 'GTM-5KMCJB6';
  const oneTrustDomainScript =
    (rest.layoutData?.sitecore?.context?.oneTrustDomainScript as Record<string, string>) || {};

  // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
  // Note Next.js does not (currently) provide anything for translation, only i18n routing.
  // If your app is not multilingual, next-localization and references to it can be removed.
  return (
    <>
      <div id={isExperienceEditor ? 'exp-editor' : undefined}>
        <ErrorBoundary>
          <I18nProvider lngDict={dictionary} locale={pageProps.locale}>
            <Component {...rest} />
          </I18nProvider>
        </ErrorBoundary>
      </div>
      <ThirdPartyScripts
        gtmId={googleTagManagerId}
        otDomainScript={oneTrustDomainScript}
        isMainSite={!isTcSite}
      />
    </>
  );
}

export type ThirdPartyScriptsProps = {
  gtmId: string;
  otDomainScript: Record<string, string>;
  isMainSite: boolean;
};

const ThirdPartyScripts = ({
  gtmId,
  otDomainScript,
  isMainSite,
}: ThirdPartyScriptsProps): JSX.Element => {
  const { isPdf } = usePdf();

  if (process.env.NODE_ENV !== 'production' || isPdf) {
    return <></>;
  }

  return (
    <>
      {/* OneTrust */}
      {otDomainScript?.value && isMainSite && (
        <Script
          id="autoblock-script"
          strategy="beforeInteractive"
          src={`https://cdn.cookielaw.org/consent/${otDomainScript?.value}/OtAutoBlock.js`}
          type="text/javascript"
        ></Script>
      )}
      {otDomainScript?.value && (
        <Script
          id="one-trust-1"
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          type="text/javascript"
          strategy="beforeInteractive"
          data-domain-script={otDomainScript?.value}
          onLoad={() => {
            `function OptanonWrapper() {}`;
          }}
        ></Script>
      )}
      {/* Google Tag Manager */}
      <Script
        id="gtm-without-cmp"
        strategy="beforeInteractive"
      >{`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}
          gtag('set','${gtmId}',true);
          gtag('consent','default',{ad_storage:"denied",analytics_storage:"denied",
            functionality_storage:"denied",personalization_storage:"denied",security_storage:"granted",
            ad_user_data:"denied",ad_personalization:"denied",
            region:["AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","IS","LI","NO","GB","BR","US-FL","US-CA"],
            'wait_for_update':500});
          gtag('consent','default',{ad_storage:"granted",analytics_storage:"granted",
            functionality_storage:"granted",personalization_storage:"granted",security_storage:"granted",
            ad_user_data:"granted",ad_personalization:"granted"});`}</Script>
      <Script
        id="google-tag-manager"
        strategy="beforeInteractive"
      >{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');`}</Script>
      <Head>
        <meta
          name="google-site-verification"
          content="oVIiQDQQ3Ce-cPUBumZHSE3Y8jy4IKH7cAVrHqJF1sQ"
        />
        <script
          id="navigate-to-single-slash-url"
          dangerouslySetInnerHTML={{
            __html: `
(function () {
  const { pathname, href } = window.location;
  if (pathname.startsWith("//")) {
    window.location.href = href.replace(pathname, pathname.replace(/^\\/{2,}/, "/"));
  }
})();`,
          }}
        />
      </Head>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        ></iframe>
      </noscript>

      {/* Font Tracking */}
      <Script
        id="font-load-1"
        strategy="lazyOnload"
      >{`var MTUserId = 'd7e3ce2e-8d0e-4d98-85ee-24bc98f711d5';var MTFontIds = [1475508, 1475514, 1475532, 1475538, 1475544];`}</Script>
      <Script id="font-load-2" src="/mtiFontTrackingCode.js" strategy="lazyOnload" />

      {/* HubSpot */}
      <Script
        id="hs-script-loader"
        src="//js.hs-scripts.com/44029032.js"
        strategy="lazyOnload"
        async
        defer
      />
    </>
  );
};

export default App;
